<script>
import { defineComponent } from 'vue';
import { Event } from '@/models/Event';
import { globalParam } from '@/helpers/globals';
import getRulesMixin from '@/mixins/get-form-rules';
import { userIsStaff } from '@/helpers/auth';
import TargetAudienceSelect from '@/components/target_audience/select.vue';
import MultiItemContainer from '@/components/multi_item/Container';
import FileUploadField from '@/components/FileUploadField';
import { fieldRuleValue } from '@/helpers/tools';
import AccessibilityUrlField from '@/components/form/AccessibilityUrlField.vue';

export default defineComponent({
    name: 'WeitereInfosPanel',
    components: {
        TargetAudienceSelect,
        MultiItemContainer,
        FileUploadField,
        AccessibilityUrlField,
    },
    mixins: [getRulesMixin],
    props: {
        record: Event,
    },
    computed: {
        openingHoursVisible() {
            return [Event.CLASS_PERMANENT_EXHIBITION].includes(this.record.event_class);
        },
    },
    created() {
        this.fieldRuleValue = fieldRuleValue;
    },
    methods: {
        globalParam,
        userIsStaff,
        onPropertyChange(change) {
            this.$emit('propertyChange', change);
        },
        onInput(fieldName, value) {
            this.onPropertyChange({ property: fieldName, value: value });
        },
    },
});
</script>

<template>
    <v-row dense>
        <v-col cols="12" lg="4">
            <LabelField
                class="mb-2"
                :label="$t('weiterfuehrende_links')"
                :label-tip="$t('weiterfuehrende_links_label_tip')"
            >
                <MultiItemContainer
                    :value="record.external_links"
                    text-name="description"
                    item-name="url"
                    :show-type-field="false"
                    item-class="mb-2"
                    @input="onInput('external_links', $event)"
                >
                    <template #text="slotProps">
                        <v-text-field
                            :value="slotProps.value"
                            :placeholder="$t('placeholder_event_additional_info_external_links_text')"
                            dense
                            outlined
                            hide-details="auto"
                            clearable
                            @change="slotProps.input"
                        />
                    </template>
                    <template #default="slotProps">
                        <UrlField
                            :value="slotProps.value"
                            :placeholder="$t('placeholder_event_additional_info_external_links_item')"
                            @change="slotProps.input"
                        />
                    </template>
                </MultiItemContainer>
            </LabelField>

            <LabelField :label="$t('socialmedia_title')">
                <v-text-field
                    :value="record.socialmedia_title"
                    :placeholder="$t('placeholder_event_info_categories_socialmedia_title')"
                    dense
                    hide-details="auto"
                    outlined
                    clearable
                    :counter="globalParam('event.max_social_media_title_chars', 70)"
                    :disabled="!record.canEditSocialMediaTitle"
                    :rules="[getRule('maxLength')(globalParam('event.max_social_media_title_chars', 70))]"
                    @change="onInput('socialmedia_title', $event)"
                />
            </LabelField>

            <LabelField :label="$t('hauptzielgruppe')">
                <TargetAudienceSelect
                    :value="record.target_audience_id"
                    :placeholder="$t('placeholder_event_additional_info_target_audience_id')"
                    :disabled="!record.canEditTargetAudience"
                    clearable
                    hide-details="auto"
                    @input="onInput('target_audience_id', $event)"
                />
            </LabelField>

            <LabelField :label="$t('altersbereich')" class="mt-6">
                <v-textarea
                    :value="record.typical_age_range"
                    rows="4"
                    dense
                    outlined
                    hide-details="auto"
                    :placeholder="$t('placeholder_typical_age_range')"
                    @change="onInput('typical_age_range', $event)"
                />
            </LabelField>
        </v-col>
        <v-col cols="12" lg="4">
            <LabelField :label="$t('vorverkauf_reservation')">
                <v-textarea
                    :value="record.standard_text_tickets"
                    :placeholder="$t('placeholder_event_additional_info_standard_text_tickets')"
                    rows="7"
                    dense
                    outlined
                    hide-details="auto"
                    counter
                    @change="onInput('standard_text_tickets', $event)"
                />
            </LabelField>
            <LabelField :label="$t('link_vorverkauf')" class="mt-6">
                <UrlField
                    :value="record.presales_link"
                    :placeholder="$t('placeholder_event_additional_info_presales_link')"
                    @change="onInput('presales_link', $event)"
                />
            </LabelField>

            <LabelField :label="$t('preise_kosten')" class="mt-6">
                <v-textarea
                    :value="record.ticketprices"
                    :placeholder="$t('placeholder_event_additional_info_ticketprices')"
                    rows="4"
                    dense
                    outlined
                    hide-details="auto"
                    @change="onInput('ticketprices', $event)"
                />
            </LabelField>

            <LabelField :label="$t('accessibility_label')" :label-tip="$t('accessibility_label_tip')" class="mt-6">
                <v-textarea
                    :value="record.accessibility"
                    rows="4"
                    dense
                    outlined
                    hide-details="auto"
                    :counter="1000"
                    :placeholder="$t('placeholder_accessibility')"
                    :rules="[getRule('maxLength')(1000)]"
                    @change="onInput('accessibility', $event)"
                />
            </LabelField>
            <LabelField :label="$t('accessibility_link')">
                <AccessibilityUrlField
                    :location-id="record.location_id"
                    :accessibility-url="record.accessibility_link"
                    @input="onInput('accessibility_link', $event)"
                />
            </LabelField>
        </v-col>
        <v-col cols="12" lg="4">
            <LabelField :label="$t('dokumente_flyer_pogrammheft')" class="mt-6">
                <MultiItemContainer
                    :value="record.documents"
                    text-name="description"
                    item-name="id"
                    :show-type-field="false"
                    item-class="mb-2"
                    @input="onInput('documents', $event)"
                >
                    <template #text="slotProps">
                        <v-text-field
                            :value="slotProps.value"
                            :placeholder="$t('placeholder_event_additional_info_documents_text')"
                            dense
                            outlined
                            hide-details="auto"
                            clearable
                            @change="slotProps.input"
                        />
                    </template>
                    <template #default="slotProps">
                        <FileUploadField
                            :value="slotProps.value"
                            :placeholder="$t('placeholder_event_additional_info_documents_item')"
                            @input="
                                (value) => {
                                    slotProps.input(value);
                                    $emit('save');
                                }
                            "
                        />
                    </template>
                </MultiItemContainer>
            </LabelField>

            <div v-if="openingHoursVisible" style="max-width: 500px">
                <h4 class="mt-5 mb-2">{{ $t('opening_hours') }}</h4>
                <p class="tgk-paragraph-text">{{ $t('opening_hours_event_tip') }}</p>
                <v-textarea
                    :value="record.opening_hours"
                    dense
                    :placeholder="$t('placeholder_schedule_opening_hours')"
                    rows="8"
                    outlined
                    hide-details="auto"
                    :counter="250"
                    :rules="[getRule('maxLength')(250)]"
                    @change="onInput('opening_hours', $event)"
                />
                <div class="d-flex">
                    <KpIcon icon-cls="info" color="grey" left size="24" style="width: 24px" />
                    <p class="tgk-paragraph-text" style="flex: 1">{{ $t('opening_hours_event_info') }}</p>
                </div>
            </div>
        </v-col>
    </v-row>
</template>

<style lang="scss" scoped>
.radio-equal {
    flex-grow: 1;
    width: 49%;
    margin-right: 0 !important;
}
</style>
